/**
 * Environment integrity checks for MFA flow.
 *
 * These checks evaluate the integrity of the browser runtime environment
 * to determine if it is secure enough to enable MFA (3DS) authentication.
 *
 * Detects SMA-injected tracking scripts/globals (e.g. Facebook/Instagram pcm.js).
 * If detected, the environment is considered NOT secure for MFA.
 */
export var MFAEnvironmentIntegritySignalType;
(function (MFAEnvironmentIntegritySignalType) {
    MFAEnvironmentIntegritySignalType[MFAEnvironmentIntegritySignalType["GLOBAL"] = 0] = "GLOBAL";
    MFAEnvironmentIntegritySignalType[MFAEnvironmentIntegritySignalType["SCRIPT"] = 1] = "SCRIPT";
})(MFAEnvironmentIntegritySignalType || (MFAEnvironmentIntegritySignalType = {}));
const RESTRICTED_MFA_SCRIPT_PATTERNS = [
    'pcm.js', // Facebook/Instagram
];
const RESTRICTED_MFA_GLOBAL_PATTERNS = [
    '_PCMNonce', // Facebook/Instagram
];
/**
 * Scans the current browser environment for known SMA-injected tracking
 * scripts and global variables that indicate a potentially compromised context.
 */
export const computeEnvironmentIntegrityForMFA = () => {
    const result = {
        detected: false,
        signals: [],
    };
    document.querySelectorAll('script[src]').forEach((script) => {
        const src = script.getAttribute('src') || '';
        RESTRICTED_MFA_SCRIPT_PATTERNS.forEach((pattern) => {
            if (src.indexOf(pattern) !== -1) {
                result.detected = true;
                result.signals.push({
                    type: MFAEnvironmentIntegritySignalType.SCRIPT,
                    pattern,
                });
            }
        });
    });
    RESTRICTED_MFA_GLOBAL_PATTERNS.forEach((pattern) => {
        if (Object.prototype.hasOwnProperty.call(window, pattern)) {
            result.detected = true;
            result.signals.push({
                type: MFAEnvironmentIntegritySignalType.GLOBAL,
                pattern,
            });
        }
    });
    return result;
};
/**
 * Returns true if the environment is secure enough for MFA flow.
 * An environment is considered secure when no restricted tracking
 * scripts or globals are detected.
 *
 * Logs a warning event via RUM when the environment is not secure.
 */
export const isEnvironmentSecureForMFA = (rumClient) => {
    const result = computeEnvironmentIntegrityForMFA();
    if (result.detected) {
        rumClient.recordEvent('ENVIRONMENT_INTEGRITY_CHECK_FAILED', {
            signals: JSON.stringify(result.signals),
        });
    }
    return !result.detected;
};
